import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { AuthSessionError, createAuthSessionCoordinator } from './auth-session-coordinator';
import { authTelemetry } from './authTelemetry';
import { useAuthRecoveryState } from '@/composables/useAuthRecoveryState';

let _userManager = null;
let _coordinator = null;
let lifecycleRegistered = false;
let lockUnavailableRecorded = false;

const isAuthSessionRenewalEnabled =
  process.env.NODE_ENV !== 'production' ||
  process.env.VUE_APP_AUTH_SESSION_RENEWAL_ENABLED === 'true';

const requireCoordinator = () => {
  if (!_coordinator) throw new Error('Auth service has not been initialized');
  return _coordinator;
};

function recordLockUnavailable() {
  if (lockUnavailableRecorded) return;
  lockUnavailableRecorded = true;
  authTelemetry.recordAuthEvent('auth_lock_unavailable', {
    outcome: 'failure',
    durationMs: 0,
  });
}

function recordRefreshResult(request, result) {
  const error = result.error;
  const timeoutCategory =
    error instanceof AuthSessionError &&
    (error.category === 'timeout' || error.category === 'lock_timeout')
      ? error.category
      : error instanceof Error && error.message.includes('timed out')
      ? 'timeout'
      : null;
  authTelemetry.recordAuthEvent(
    timeoutCategory === 'lock_timeout' ? 'auth_lock_timeout' : 'auth_refresh',
    {
      outcome: error ? (timeoutCategory ? 'timeout' : 'failure') : 'success',
      reason: request.reason,
      durationMs: result.durationMs,
      ...(result.lockWaitMs > 1_000 ? { lockWaitMs: result.lockWaitMs } : {}),
      ...(error ? { errorCategory: timeoutCategory || 'unknown' } : {}),
    },
  );
  if (result.user?.access_token) authTelemetry.flushAuthEvents(result.user.access_token);
}

function registerLifecycleTriggers() {
  if (lifecycleRegistered) return;
  lifecycleRegistered = true;
  const ensure = (reason) =>
    ensureValidSession(reason).catch(() => signInRedirect().catch(() => undefined));
  _userManager.events.addAccessTokenExpiring(() => ensure('token_expiring'));
  window.addEventListener('focus', () => ensure('focus'));
  window.addEventListener('online', () => ensure('online'));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') ensure('visibility');
  });
}

function init() {
  const tenantId = localStorage.getItem('tenant_id');
  if (!_userManager) {
    const settings = {
      authority: process.env.VUE_APP_OIDC_AUTHORITY,
      client_id: process.env.VUE_APP_OIDC_CLIENT_ID,
      redirect_uri: `${window.location.origin}${process.env.VUE_APP_BASE_PATH}signin-oidc`,
      post_logout_redirect_uri: `${window.location.origin}${process.env.VUE_APP_BASE_PATH}signout-oidc`,
      response_type: 'code',
      scope: process.env.VUE_APP_OIDC_SCOPES,
      userStore: new WebStorageStateStore(),
      loadUserInfo: true,
      automaticSilentRenew: !isAuthSessionRenewalEnabled,
      silentRequestTimeoutInSeconds: 15,
      redirectTarget: 'top',
    };

    if (tenantId) {
      settings.authority = `${settings.authority.replace(/\/+$/, '')}/company/${tenantId}`;
    }
    _userManager = new UserManager(settings);
    _coordinator = createAuthSessionCoordinator({
      getUser: () => _userManager.getUser(),
      signinSilent: ({ timeoutMs }) =>
        _userManager.signinSilent({ silentRequestTimeoutInSeconds: timeoutMs / 1000 }),
      signinRedirect: (options) => _userManager.signinRedirect(options),
      authority: settings.authority,
      clientId: settings.client_id,
      useWebLock: isAuthSessionRenewalEnabled,
      onLockUnavailable: recordLockUnavailable,
      onRefreshSettled: recordRefreshResult,
    });
    if (isAuthSessionRenewalEnabled && !navigator.locks) recordLockUnavailable();
    if (isAuthSessionRenewalEnabled) registerLifecycleTriggers();
  }
}

async function signInRedirect(options = null) {
  if (isAuthSessionRenewalEnabled) {
    const recovery = useAuthRecoveryState();
    recovery.setRetry(() => signInRedirect(options));
    authTelemetry.recordAuthEvent('auth_interactive_redirect', {
      outcome: 'success',
      reason: 'startup_expired',
      durationMs: 0,
    });
    try {
      await requireCoordinator().beginSignIn(options || undefined);
      return;
    } catch (error) {
      recovery.showFailure();
      throw error;
    }
  }
  if (options) {
    return await _userManager.signinRedirect(options);
  }
  return await _userManager.signinRedirect();
}

function signInCallback() {
  return _userManager.signinCallback();
}

async function signoutRedirect(options = {}) {
  return await _userManager.signoutRedirect({
    post_logout_redirect_uri: `${window.location.origin}${process.env.VUE_APP_BASE_PATH}signout-oidc`,
    ...options,
  });
}

function signoutCallback() {
  return _userManager.signoutCallback();
}

async function getUser() {
  const user = await _userManager.getUser();
  return user;
}

async function getAccessToken() {
  if (isAuthSessionRenewalEnabled) return requireCoordinator().getValidAccessToken();
  const user = await _userManager.getUser();
  return user?.access_token;
}

async function getValidUser() {
  if (!isAuthSessionRenewalEnabled) return _userManager.getUser();
  return requireCoordinator().getValidUser();
}

async function refreshSession(request) {
  return requireCoordinator().refreshSession(request);
}

async function ensureValidSession(reason = 'startup_expired') {
  if (!isAuthSessionRenewalEnabled) {
    const user = await _userManager.getUser();
    if (!user) throw new Error("User doesn't exist");
    return user;
  }
  const valid = await requireCoordinator().getValidUser();
  if (valid?.access_token && (reason === 'startup_expired' || reason === 'online'))
    authTelemetry.flushAuthEvents(valid.access_token);
  return valid || refreshSession({ reason });
}

function changeTenantId(tenantId) {
  localStorage.setItem('tenant_id', tenantId);
}

function removeTenantId(tenantId) {
  localStorage.removeItem('tenant_id', tenantId);
}

export default {
  init,
  signInRedirect,
  signInCallback,
  getUser,
  getValidUser,
  getAccessToken,
  refreshSession,
  ensureValidSession,
  beginSignIn: signInRedirect,
  isAuthSessionRenewalEnabled,
  signoutRedirect,
  signoutCallback,
  changeTenantId,
  removeTenantId,
};

// export default class AuthService {
//     userManager: UserManager

//     constructor() {

//     public signInRedirect() {
//       return this.userManager.signinRedirect()
//     }

//     public signInCallback() {
//       return this.userManager.signinCallback()
//     }

//     public renewToken(): Promise<void> {
//       return this.userManager.signinSilentCallback()
//     }

//     public logout(): Promise<void> {
//       return this.userManager.signoutRedirect()
//     }

//     public getUser(): Promise<User | null> {
//       return this.userManager.getUser()
//     }
//   }
